import React, { useCallback, useEffect, useState } from "react";
import { colors, gaps, padding } from "theme/defaultStyle";
import StewartTable from "controls/global/stewart-table";
import { Box, debounce, styled } from "@mui/material";
import { StewartTableColumn } from "controls/global/stewart-table/StewartTable";
import TooltipCell from "controls/global/stewart-table/TooltipCell";
import ViewMessageDialog from "./ViewMessageDialog";
import useFormWrapper from "utils/custom-hooks/useFormWrapper";
import { useGlobalAccess } from "utils/context/GlobalAccessContext";
import { showToast } from "controls/global/stewart-toast/showToast";
import {
  useHasSentMessageSuccessfully,
  useMessage,
} from "utils/context/TitleMessageContext";
import { Order } from "utils/sorting";
import { useConfigContext } from "utils/context/ConfigContextProvider";
import { useMyFiles } from "utils/context/MyFilesContext";
import { Message, UWAProduct } from "entities/UIModel";
import { formatDateRegEx } from "utils/shared";
import { UWAMessagesGridCriteria } from "entities/UIModel/UWAMessageGridCriteria";
// import { mapToUiMessages } from "utils/data/messageGridhelper";

const StyledTableContainer = styled(Box)(() => ({
  display: "flex",
  flexDirection: "column",
  overflowX: "hidden",
  gap: gaps.large1,
  paddingBottom: padding.xlarge,
  "& .MuiTableContainer-root": {
    minHeight: "80px",
    maxHeight: "292px",
    overflowX: "auto",
    paddingLeft: padding.xlarge0,
  },
  "& td:first-of-type": {
    backgroundColor: "unset !important",
  },
  "& .MuiTableContainer-root::-webkit-scrollbar": {
    height: "12px  !important",
    width: "12px  !important",
  },
  "& .MuiTableContainer-root::-webkit-scrollbar-track": {
    background: `${colors.grey21} !important`,
    boxShadow: "1px 0px 0px 0px #DEDEDE inset !important",
  },
  "& .MuiTableContainer-root::-webkit-scrollbar-thumb": {
    borderRadius: "9px !important",
    backgroundColor: `${colors.grey20} !important`,
    border: "3px solid transparent !important",
    backgroundClip: "content-box",
  },

  "& .MuiTableCell-root": {
    paddingBottom: padding.medium1,
    paddingLeft: `${padding.small1} !important`,
    paddingRight: `${padding.small1} !important`,
    paddingTop: padding.small1,
    "& > *": {
      lineHeight: "100%",
    },
    "& .MuiButtonBase-root": {
      padding: padding.zero,
    },
    // override row-header-cell for checbox
    "&.row-header-cell": {
      backgroundColor: "unset",
    },
  },

  "&:hover": {
    backgroundColor: "none",
  },

  "& tr #messageText": {
    maxWidth: "20% !important",
  },

  "& thead": {
    "& tr th#messageText": {
      minWidth: "30% !important",
      maxWidth: "30% !important",
    },
    "& tr th#messageUploadedDate": {
      width: "170px !important",
      whiteSpace: "nowrap",
    },
    "& tr th#messageUploadedByUserName": {
      width: "200px !important",
      whiteSpace: "nowrap",
    },
    "& tr th#messageSource": {
      width: "120px !important",
      whiteSpace: "nowrap",
    },
    "& tr th#Actions": {
      width: "90px !important",
      whiteSpace: "nowrap",
    },
  },
  "& tbody .MuiTableRow-root": {
    "&.resware": {
      backgroundColor: `${colors.lightBlue} !important`,
    },
  },
  "& tbody tr": {
    "&:hover": {
      backgroundColor: "white",
    },
  },
  "& tbody tr.toggled .MuiTableCell-root": {
    color: colors.grey11,
  },
}));

interface Props {
  productIndex: number;
}

const UWAMessagesGrid: React.FC<Props> = ({ productIndex }) => {
  const [data, setData] = useState<Message[]>([]);
  const { getValues } = useFormWrapper();
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
  const InitialMessage = "Loading results...";
  const [noRecordsMessage, setNoRecordsMessage] = useState<string[]>([
    InitialMessage,
  ]);
  const [filters, setFilters] = useState<any>(undefined);
  const [sortColumn, setSortColumn] = useState<string | undefined>(undefined);
  const [readyToSearch, setReadyToSearch] = useState<boolean>(false);
  const [selectedMessage, setSelectedMessage] = useState<Message | null>(null);
  let orderId: number = getValues(`uwaProducts.${productIndex}.orderID`) || 0;

  const [, { setRequestTimeoutMs }] = useMyFiles();

  const [{ selectedAgency: globalAccessAgency }] = useGlobalAccess();
  const globalAccessAgencyID = globalAccessAgency?.[0]?.CompanyID;
  const NO_RECORD_MESSAGE = "No Messages found";
  const [order, setOrder] = React.useState<Order>("desc");
  const [, { getMessageByOrder }] = useMessage();
  const FETCHING_DATA_ERROR_MESSAGE =
    "There was a problem fetching your messages. Please try again";
  const [orderBy, setOrderBy] = React.useState<keyof Message | undefined>(
    "messageCreatedDate"
  );
  const { generalConfig } = useConfigContext();
  const [userInputDebounceTimeMs, setUserInputDebounceTimeMs] =
    useState<number>(750);
  const [settingsLoaded, setSettingsLoaded] = useState<boolean>(false);
  const currentProduct: UWAProduct = getValues(`uwaProducts.${productIndex}`);
  const [{ hasSentMessageSuccessfully, isLoading }] =
    useHasSentMessageSuccessfully();

  const handleRowClick = (messageID: number) => {
    const message = data.find((d) => d.messageID === messageID);
    if (message) {
      setSelectedMessage(message);
      setIsDialogOpen(true);
    }
  };

  const handleRequestSort = async (columnName: any) => {
    let sortBy = columnName;
    if (sortColumn === sortBy) {
      sortBy = `-${sortBy}`;
    }

    setOrder(sortBy.startsWith("-") ? "desc" : "asc");
    setOrderBy(columnName.replace("-", "") as keyof Message);
    setSortColumn(sortBy);
  };

  const getDefaultFilters = useCallback((): UWAMessagesGridCriteria => {
    const sortColumn = "-messageCreatedDate";
    return {
      fileId: getValues("id"),
      orderId: getValues(`uwaProducts.${productIndex}.orderID`),
      listCount: 0,
      requestedPage: 1,
      isInitialLoad: false,
      sortColumn: sortColumn,
    };
  }, [getValues, productIndex]);

  const getMessagesSearchCriteria = () => {
    let criteria: UWAMessagesGridCriteria = {};

    criteria = {
      ...filters,
      listCount: 0,
      requestedPage: 1,
      ...(globalAccessAgencyID && { companyId: globalAccessAgencyID }),
    };
    if (sortColumn) {
      criteria = {
        ...criteria,
        sortColumn: sortColumn,
      };
    }
    return criteria;
  };

  const performSearch = async () => {
    let criteria = getMessagesSearchCriteria();
    let messages: Message[] | undefined = [];
    if ((criteria.orderId || 0) > 0) {
      try {
        messages = await getMessageByOrder({ ...criteria });
        if (!messages) setNoRecordsMessage([NO_RECORD_MESSAGE]);
        if (messages !== undefined) {
          const updatedMessages = messages.map((m) => ({
            ...m,
            rowClassName:
              m.originationSourceCode === "A" 
                ? "background-blue"
                : "",
          }));

          setData(updatedMessages);
          if (!messages?.length) {
            setNoRecordsMessage([NO_RECORD_MESSAGE]);
          }
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        showToast({
          type: "error",
          title: FETCHING_DATA_ERROR_MESSAGE,
        });
      }
    }
  };

  const canSearch = (): boolean => {
    return filters !== undefined && Object.values(filters).length > 0;
  };

  useEffect(() => {
    if (readyToSearch) {
      const search = async () => {
        await performSearch();
        setReadyToSearch(false);
      };

      search();
    }
  }, [readyToSearch]);

  const loadSettings = async () => {
    const debounceTime = generalConfig.userInputDebounceTimeMs;
    const timeoutMs = generalConfig.filesSearchTimeoutMs;

    if (debounceTime) {
      setUserInputDebounceTimeMs(debounceTime);
    }
    if (timeoutMs) {
      setRequestTimeoutMs(timeoutMs);
    }
  };
  const debounceSetReadyToSearch = useCallback(
    debounce(() => setReadyToSearch(true), userInputDebounceTimeMs),
    [userInputDebounceTimeMs]
  );

  useEffect(() => {
    const init = async () => {
      await loadSettings();
      setSettingsLoaded(true);
    };

    init();
  }, []);

  useEffect(() => {
    if (hasSentMessageSuccessfully) {
      if (!isLoading) {
        performSearch();
      }
    }
  }, [hasSentMessageSuccessfully]);

  const columns: StewartTableColumn<any>[] = [
    {
      field: "messageText",
      name: "MESSAGES",
      // width: 472,
      valueGetter: (row: Message) => {
        const isHidden = row.isHidden === 1;

        return (
          <Box
            onClick={() => handleRowClick(row.messageID)}
            sx={{
              cursor: "pointer",
              color: isHidden ? colors.grey11 : "inherit",
            }}
          >
            <TooltipCell checkDataLimit={true} title={row.messageText || ""} />
          </Box>
        );
      },
    },
    {
      field: "messageCreatedDate",
      name: "SENT DATE/TIME",
      sortable: true,
      width: 200,
      valueGetter: (row: Message) => {
        const isHidden = row.isHidden === 1;
        return (
          <Box sx={{ color: isHidden ? colors.grey11 : "inherit" }}>
            <TooltipCell
              title={formatDateRegEx(row.messageCreatedDate || "") || ""}
            />
          </Box>
        );
      },
    },
    {
      field: "originationUser",
      name: "SENT BY",
      width: 300,
      valueGetter: (row: Message) => {
        const isHidden = row.isHidden === 1;

        return (
          <Box sx={{ color: isHidden ? colors.grey11 : "inherit" }}>
            <TooltipCell title={row.originationUser || ""} />
          </Box>
        );
      },
    },
    {
      field: "originationSource",
      name: "SOURCE",
      width: 250,
      valueGetter: (row: Message) => {
        const isHidden = row.isHidden === 1;

        return (
          <Box sx={{ color: isHidden ? colors.grey11 : "inherit" }}>
            <TooltipCell title={row.originationSource || ""} />
          </Box>
        );
      },
    },
  ];

  useEffect(() => {
    const defaultFilters = getDefaultFilters();
    setFilters(defaultFilters);
  }, [currentProduct, getDefaultFilters]);

  useEffect(() => {
    if (canSearch()) {
      debounceSetReadyToSearch();
    }
  }, [filters, sortColumn, settingsLoaded, orderId]);

  return (
    <>
      {data?.length > 0 && (
        <StyledTableContainer>
          <StewartTable
            cols={columns}
            rows={data.map((row) => ({
              ...row,
            }))}
            page={1}
            rowsPerPage={9999}
            showActionRow={false}
            onColumnResize={() => {}}
            orderBy={orderBy}
            order={order}
            onRequestSort={handleRequestSort}
            noRowsMessage={noRecordsMessage}
          />

          {selectedMessage && (
            <ViewMessageDialog
              isOpen={isDialogOpen}
              onClose={() => setIsDialogOpen(false)}
              message={selectedMessage}
            />
          )}
        </StyledTableContainer>
      )}
    </>
  );
};

export default UWAMessagesGrid;
